import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Moon, Shield, Sun, User } from 'lucide-react';
import clsx from 'clsx';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/useAuthStore';
import { IconButton } from '../components/ui';
import UserMenuDropdown from '../components/UserMenuDropdown';

// Matches IconButton's own visual treatment exactly, since this needs to be
// a router <Link> (for a real href) rather than a <button> — IconButton only
// renders the latter.
const iconLinkClass = clsx(
  'inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors',
  'hover:bg-elevated hover:text-body',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
);

export function TopBar() {
  const isDarkMode = useThemeStore((state) => state.isDarkMode);
  const toggleDarkMode = useThemeStore((state) => state.toggleDarkMode);
  const email = useAuthStore((state) => state.email);
  const role = useAuthStore((state) => state.role);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="flex h-12 items-center justify-between border-b border-edge bg-panel/80 px-4 backdrop-blur">
      <Link to="/app" className="font-sans font-bold bg-accent-gradient bg-clip-text text-transparent">
        ✦ FlowCraftr
      </Link>
      <div className="flex items-center gap-1">
        {role === 'admin' && (
          <Link to="/app/admin" aria-label="Admin" className={iconLinkClass}>
            <Shield className="h-4 w-4" />
          </Link>
        )}
        <IconButton
          label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={toggleDarkMode}
        >
          {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </IconButton>
        <div ref={menuRef} className="relative">
          <IconButton label="Account menu" onClick={() => setMenuOpen((v) => !v)}>
            <User className="h-4 w-4" />
          </IconButton>
          {menuOpen && <UserMenuDropdown email={email} onLogout={handleLogout} position="bottom" />}
        </div>
      </div>
    </header>
  );
}
